<script lang="ts" setup>
import AUApi from "@/api";
import { ref, onMounted, reactive, nextTick } from "vue";
import { goBack } from "@/NavigationHelper";

import { getKennungValueDisplay, getKennungTitle, getKennungValue } from "@/DataHelper";
import CommonBaseButtons from "@/components/common/BaseButtons.vue";
import { mountedBase, onSave, BaseSetup } from "@/DataLoaderStatic";
import {
  createEditorValidator,
  provideEditorValidator,
} from "@/components/EditorValidator";
import { showSnackbar } from "@/UIHelper";
const validator = createEditorValidator();
provideEditorValidator(validator);

const emit = defineEmits(["back", "delete"]);
const props = defineProps<{
  zeitbereichUebergabe?: APIZeitbereich | null;
}>();

let anzeige: boolean = true;
let anzeigeAntrag: boolean = true;
let deleteVisible = ref<boolean>(false);
let saveVisible = ref<boolean>(false);
let showUestAuszahlen: boolean = false;
let showauszahlungUest: boolean = false;
let showZulage1: string = "";
let showZulage2: string = "";
let showZulage3: string = "";
const pageParams: DataPageParameter = {
  zeitbereich: AUApi.Current.zeitbereich,
  insertMode: false,
  query: "Web_WF_Antrag_Tag",
  emitBack: () => emit("back"),
};
const uiData = reactive<DataPageUIData>(BaseSetup(pageParams));
onMounted(async () => {
  if (props.zeitbereichUebergabe) {
    const von = props.zeitbereichUebergabe.von;
    const bis = props.zeitbereichUebergabe.von;
    pageParams.zeitbereich = {
      von,
      bis,
      bereich: "Tag",
    };
  }
  await mountedBase(uiData, pageParams);
  if (!uiData.showLoading && uiData.data!) {
    if (uiData.data && uiData.data[0].aic_Bew_pool) {
      deleteVisible.value = true;
    }
    const keinAntrag = getKennungValueDisplay(
      uiData.data[0],
      "keinAntrag",
      uiData.columns
    );

    const editierbar = getKennungValue(uiData.data[0], "ZE_ANTRAG_BOOL3", uiData.columns);
    if (editierbar === 1) {
      saveVisible.value = false;
      deleteVisible.value = false;
    }
    if (keinAntrag === "1.00") {
      anzeigeAntrag = false;
    }
    const uestAuszahlen = getKennungTitle("ZE_UEST_NICHT_AUSZAH", uiData.columns);
    if (uestAuszahlen) {
      showUestAuszahlen = true;
    }
    const auszahlungUest = getKennungTitle("ZE_AUSZAHLUNG_UEST", uiData.columns);
    if (auszahlungUest) {
      showauszahlungUest = true;
    }
    showZulage1 = getKennungTitle("Zulage_1", uiData.columns);
    showZulage2 = getKennungTitle("Zulage_2", uiData.columns);
    showZulage3 = getKennungTitle("Zulage_3", uiData.columns);
  }
  uiData.showLoading = false;
  uiData.initialized = true;
});

async function bewDelete() {
  if (uiData.data[0] && uiData.data[0].aic_Bew_pool) {
    emit("delete", uiData.data[0].aic_Bew_pool);
  }
}
async function save() {
  const { valid, invalidFields } = await validator.validateAsync();
  if (!valid) {
    showSnackbar({
      text:
        "Validierung fehlgeschlagen - Bitte prüfen! (" + invalidFields.join(", ") + ")",
      duration: 4000,
      color: "error",
    });
    return;
  }
  await onSave(uiData, pageParams, 0, "");
  goBack();
}
</script>
<template>
  <v-card :loading="uiData.showLoading" v-if="uiData.initialized">
    <v-toolbar class="subtitle-1 white--text dvhBackground lighten-2">
      <span class="pl-1" style="color: white">{{ uiData.header.bezeichnung }} </span>
      <span>: </span>
      <span v-if="anzeigeAntrag" class="pl-2">{{
        getKennungValueDisplay(uiData.data[0], "ZE_ANTRAG_BOOL3", uiData.columns)
      }}</span>
    </v-toolbar>
    <v-card-text class="cardText pt-2">
      <v-row v-if="anzeige">
        <v-col class="colNoPad" cols="12" sm="6">
          <EditCheckBox kennung="ZE_KEINE_VERFALLBEGI" />
        </v-col>

        <v-col class="colNoPad" cols="12" sm="6">
          <EditCheckBox kennung="ZE_K_VERFALLZEITENDE" />
        </v-col>
      </v-row>
      <v-row v-if="anzeige && (showUestAuszahlen || showauszahlungUest)">
        <v-col class="colNoPad" cols="12" sm="6" v-if="showUestAuszahlen">
          <EditCheckBox kennung="ZE_UEST_NICHT_AUSZAH" />
        </v-col>
        <v-col class="colNoPad" cols="12" sm="6" v-if="showauszahlungUest">
          <EditTimePicker kennung="ZE_AUSZAHLUNG_UEST" />
        </v-col>
      </v-row>
      <v-row v-if="anzeige && showZulage1">
        <v-col class="colNoPad" cols="12" sm="6">
          <v-sheet grow pa-1>
            <div>
              {{ showZulage1 }}:
              {{ getKennungValueDisplay(uiData.data[0], "Zulage_1", uiData.columns) }}
            </div>
          </v-sheet>
        </v-col>

        <v-col class="colNoPad" cols="12" sm="6" v-if="showZulage2">
          <v-sheet grow pa-1>
            <div>
              {{ showZulage2 }}:
              {{ getKennungValueDisplay(uiData.data[0], "Zulage_2", uiData.columns) }}
            </div>
          </v-sheet>
        </v-col>
        <v-col class="colNoPad" cols="12" sm="6" v-if="showZulage3">
          <v-sheet grow pa-1>
            <div>
              {{ showZulage3 }}:
              {{ getKennungValueDisplay(uiData.data[0], "Zulage_3", uiData.columns) }}
            </div>
          </v-sheet>
        </v-col>
      </v-row>
      <v-row v-if="anzeige" class="mt-0">
        <v-col class="colNoPad" cols="12" sm="6">
          <EditCheckBox kennung="ZE_KEINE_PAUSE" :autofocus="true" />
        </v-col>
        <v-col class="colNoPad" cols="12" sm="6">
          <EditTimePicker kennung="ZE_ALTERNATIVE_PAUSE" />
        </v-col>
      </v-row>
      <v-row v-if="!anzeige">
        <v-col class="colNoPad" cols="12" sm="6">
          <EditCheckBox :disabled="true" kennung="ZE_KEINE_PAUSE" />

          <EditCheckBox :disabled="true" kennung="ZE_KEINE_VERFALLBEGI" />
          <EditCheckBox :disabled="true" kennung="ZE_K_VERFALLZEITENDE" />
        </v-col>
        <v-col class="py-0" cols="12" sm="6">
          <EditTimePicker :disabled="true" ma-0 kennung="ZE_ALTERNATIVE_PAUSE" />
          <EditCheckBox
            v-if="showUestAuszahlen"
            :disabled="true"
            kennung="ZE_UEST_NICHT_AUSZAH"
          />
          <EditTimePicker
            v-if="showauszahlungUest"
            ma-0
            :disabled="true"
            kennung="ZE_AUSZAHLUNG_UEST"
          />
        </v-col>
      </v-row>
      <EditTextArea clearable="false" v-if="anzeige" kennung="MEMO_MITARBEITER" />
      <EditTextArea
        clearable="false"
        v-if="!anzeige"
        :disabled="true"
        kennung="MEMO_MITARBEITER"
      />
      <EditTextArea kennung="VG" />
    </v-card-text>
    <v-card-actions class="py-0">
      <v-spacer></v-spacer>
      <CommonBaseButtons
        :saveVisible="saveVisible"
        :backVisible="false"
        :deleteVisible="deleteVisible"
        :createVisible="false"
        :refreshVisible="false"
        @delete="bewDelete"
        @save="save"
      ></CommonBaseButtons>
    </v-card-actions>
  </v-card>
</template>

<style scoped>
.dvhCard {
  margin-bottom: 0px;
  width: 100%;
  padding-top: 0px;
  padding-left: 5px;
  padding-right: 5px;
  /* overflow-y: scroll; */
}
.cardText {
  text-align: left;
  padding-top: 0px;
  padding-bottom: 0px;
}
.dvhCol {
  padding-right: 0px;
  padding-left: 10px;
  padding-top: 15px;
  padding-bottom: 0px;
}
.colNoPad {
  padding: 0 0 0 0;
}
.white--text {
  color: white;
}
</style>
